
import React, { useState } from 'react';
import { FormData, TableShape, Theme, CustomElementFormData } from '../types';
import { DANCE_FLOOR_DIMENSIONS_BY_GUESTS } from '../constants';
import { PlusIcon, TrashIcon } from './icons';

interface InputFormProps {
  formData: FormData;
  onFormChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, 
    field?: keyof FormData | `mainTableData.${keyof FormData['mainTableData']}` | `customElements.${number}.${keyof CustomElementFormData}`
  ) => void;
  onAddCustomElement: () => void;
  onRemoveCustomElement: (id: string) => void;
  onGeneratePlan: () => void; 
  onResetPlan: () => void;
  theme: Theme;
}

const InputForm: React.FC<InputFormProps> = ({
  formData,
  onFormChange,
  onAddCustomElement,
  onRemoveCustomElement,
  onGeneratePlan, 
  onResetPlan,
  theme,
}) => {
  // Estado local para mostrar/ocultar medidas personalizadas
  const [showCustomDanceFloorDims, setShowCustomDanceFloorDims] = useState(
    !!(formData.danceFloorCustomWidth || formData.danceFloorCustomHeight)
  );

  const inputBgClass = theme === 'dark' ? 'bg-hba-light-purple border-hba-mid-purple' : 'bg-gray-50 border-gray-300';
  const labelTextClass = theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-700';
  const sliderValueTextClass = theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900';
  const buttonClass = `w-full py-3 px-4 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50`;
  const primaryButtonClass = `${buttonClass} ${theme === 'dark' ? 'bg-hba-accent-purple text-white hover:bg-purple-600 focus:ring-hba-accent-purple' : 'bg-hba-accent-purple text-white hover:bg-purple-700 focus:ring-hba-accent-purple'}`;
  const secondaryButtonClass = `${buttonClass} ${theme === 'dark' ? 'bg-hba-light-purple hover:bg-hba-mid-purple text-hba-text-primary' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} focus:ring-gray-400`;
  const iconButtonClass = `p-2 rounded-md transition-colors ${theme === 'dark' ? 'text-hba-accent-pink hover:bg-hba-light-purple/70' : 'text-hba-accent-purple hover:bg-purple-100'}`;
  const numberInputClass = `w-20 p-1 text-sm text-center border rounded-md ml-2 ${inputBgClass} ${theme === 'dark' ? 'text-white' : 'text-black'}`;

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof FormData) => {
    onFormChange(e, fieldName);
  };
  
  const handleMainTableInputChange = (e: React.ChangeEvent<HTMLInputElement>, subField: keyof FormData['mainTableData']) => {
    onFormChange(e, `mainTableData.${subField}`);
  };

  const handleCustomElementChange = (
    e: React.ChangeEvent<HTMLInputElement>, 
    index: number, 
    subField: keyof CustomElementFormData
  ) => {
    onFormChange(e, `customElements.${index}.${subField}`);
  };

  const getSuggestedDanceFloorDims = () => {
    const numGuests = parseInt(formData.guests);
    if (isNaN(numGuests)) return "N/A";
    const dim = DANCE_FLOOR_DIMENSIONS_BY_GUESTS.find(d => numGuests >= d.minGuests && numGuests <= d.maxGuests) || 
                DANCE_FLOOR_DIMENSIONS_BY_GUESTS[DANCE_FLOOR_DIMENSIONS_BY_GUESTS.length - 1];
    return `${dim.width}m x ${dim.height}m`;
  };

  return (
    <div className={`p-6 rounded-lg shadow-xl ${theme === 'dark' ? 'bg-hba-mid-purple' : 'bg-white'}`}>
      <h2 className={`text-xl font-bold mb-6 flex items-center ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-800'}`}>
        <span className="bg-hba-accent-purple w-2 h-6 mr-3 rounded-full"></span>
        Configuración del Evento
      </h2>
      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {/* Venue Dimensions */}
        <div className="grid grid-cols-1 gap-4">
             <div>
                <div className="flex justify-between mb-1">
                    <label htmlFor="venueWidth" className={`text-sm font-medium ${labelTextClass}`}>Ancho Salón (m)</label>
                </div>
                <div className="flex items-center">
                    <input type="range" name="venueWidth" id="venueWidth" value={formData.venueWidth} onChange={(e) => handleSliderChange(e, 'venueWidth')} min="5" max="100" step="0.5" className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${theme === 'dark' ? 'bg-hba-light-purple accent-hba-accent-pink' : 'bg-gray-300 accent-hba-accent-purple'}`} />
                    <input type="number" value={formData.venueWidth} onChange={(e) => handleSliderChange(e, 'venueWidth')} className={numberInputClass} step="0.5" min="5" max="100" />
                </div>
            </div>
            <div>
                <div className="flex justify-between mb-1">
                    <label htmlFor="venueLength" className={`text-sm font-medium ${labelTextClass}`}>Largo Salón (m)</label>
                </div>
                <div className="flex items-center">
                    <input type="range" name="venueLength" id="venueLength" value={formData.venueLength} onChange={(e) => handleSliderChange(e, 'venueLength')} min="5" max="100" step="0.5" className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${theme === 'dark' ? 'bg-hba-light-purple accent-hba-accent-pink' : 'bg-gray-300 accent-hba-accent-purple'}`} />
                    <input type="number" value={formData.venueLength} onChange={(e) => handleSliderChange(e, 'venueLength')} className={numberInputClass} step="0.5" min="5" max="100" />
                </div>
            </div>
        </div>

        {/* Guests */}
        <div>
           <div className="flex justify-between mb-1">
              <label htmlFor="guests" className={`text-sm font-medium ${labelTextClass}`}>Total Invitados</label>
           </div>
           <div className="flex items-center">
              <input type="range" name="guests" id="guests" value={formData.guests} onChange={(e) => handleSliderChange(e, 'guests')} min="10" max="500" step="1" className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${theme === 'dark' ? 'bg-hba-light-purple accent-hba-accent-pink' : 'bg-gray-300 accent-hba-accent-purple'}`} />
              <input type="number" value={formData.guests} onChange={(e) => handleSliderChange(e, 'guests')} className={numberInputClass} min="1" max="1000" />
           </div>
        </div>

        {/* Standard Table Config */}
        <div className="p-4 rounded-md border border-opacity-20 border-gray-400 bg-opacity-5 bg-gray-400">
          <h3 className={`text-sm font-semibold mb-3 ${sliderValueTextClass}`}>Configuración de Mesas</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="tableShape" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Forma</label>
              <select name="tableShape" id="tableShape" value={formData.tableShape} onChange={(e) => onFormChange(e,'tableShape')} className={`w-full p-2 border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`}>
                <option value="round">Redonda</option>
                <option value="rectangular">Rectangular</option>
                <option value="square">Cuadrada</option>
                <option value="living">Living</option>
              </select>
            </div>
            <div>
                <div className="flex justify-between mb-1">
                     <label htmlFor="seatsPerStandardTable" className={`block text-xs font-medium ${labelTextClass}`}>Asientos por Mesa</label>
                </div>
                <div className="flex items-center">
                    <input type="range" name="seatsPerStandardTable" id="seatsPerStandardTable" value={formData.seatsPerStandardTable} onChange={(e) => handleSliderChange(e, 'seatsPerStandardTable')} min="4" max="12" step="1" className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${theme === 'dark' ? 'bg-hba-light-purple accent-hba-accent-pink' : 'bg-gray-300 accent-hba-accent-purple'}`} />
                    <span className={`ml-3 font-bold w-8 text-center ${sliderValueTextClass}`}>{formData.seatsPerStandardTable}</span>
                </div>
            </div>
          </div>
        </div>
        
        {/* Dance Floor Configuration */}
        <div className="border-t pt-4 border-gray-500/30">
            <div className="flex items-center justify-between">
                <label htmlFor="danceFloorEnabled" className={`text-sm font-medium flex items-center gap-2 ${labelTextClass}`}>
                    <span className="text-lg">💃</span> Incluir Pista de Baile
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                <input 
                    type="checkbox" 
                    name="danceFloorEnabled" 
                    id="danceFloorEnabled" 
                    checked={formData.danceFloorEnabled} 
                    onChange={(e) => onFormChange(e,'danceFloorEnabled')}
                    className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-hba-accent-purple/30 dark:peer-focus:ring-hba-accent-pink/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-hba-accent-purple"></div>
                </label>
            </div>
            {formData.danceFloorEnabled && (
              <div className="mt-3 space-y-3 pl-4 border-l-2 border-hba-accent-pink/50 transition-all">
                <p className={`text-xs ${labelTextClass} bg-opacity-10 bg-white p-2 rounded`}>Dimensiones Sugeridas: <span className="font-bold">{getSuggestedDanceFloorDims()}</span></p>
                
                <div className="flex items-center mt-2">
                    <input 
                        type="checkbox" 
                        id="toggleCustomDims" 
                        checked={showCustomDanceFloorDims} 
                        onChange={(e) => setShowCustomDanceFloorDims(e.target.checked)}
                        className="w-4 h-4 rounded text-hba-accent-purple focus:ring-hba-accent-purple/50 bg-gray-100 border-gray-300"
                    />
                    <label htmlFor="toggleCustomDims" className={`ml-2 text-xs font-medium cursor-pointer ${theme === 'dark' ? 'text-hba-accent-pink' : 'text-hba-accent-purple'}`}>
                        Personalizar medidas
                    </label>
                </div>

                {showCustomDanceFloorDims && (
                    <div className="grid grid-cols-2 gap-2 animate-fade-in-down">
                        <div>
                        <label htmlFor="danceFloorCustomWidth" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Ancho (m)</label>
                        <input 
                            type="number" 
                            name="danceFloorCustomWidth" 
                            id="danceFloorCustomWidth" 
                            value={formData.danceFloorCustomWidth || ''} 
                            onChange={(e) => onFormChange(e, 'danceFloorCustomWidth')} 
                            min="1" 
                            step="0.1" 
                            placeholder="Sugerido"
                            className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} 
                        />
                        </div>
                        <div>
                        <label htmlFor="danceFloorCustomHeight" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Alto (m)</label>
                        <input 
                            type="number" 
                            name="danceFloorCustomHeight" 
                            id="danceFloorCustomHeight" 
                            value={formData.danceFloorCustomHeight || ''} 
                            onChange={(e) => onFormChange(e, 'danceFloorCustomHeight')} 
                            min="1" 
                            step="0.1" 
                            placeholder="Sugerido"
                            className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} 
                        />
                        </div>
                    </div>
                )}
              </div>
            )}
        </div>

        {/* Main Table Configuration */}
        <div className="border-t pt-4 border-gray-500/30">
            <div className="flex items-center justify-between mb-3">
                <label htmlFor="enableMainTable" className={`text-sm font-medium flex items-center gap-2 ${labelTextClass}`}>
                    <span className="text-lg">👑</span> Mesa Principal
                </label>
                <label className="relative inline-flex items-center cursor-pointer">
                <input 
                    type="checkbox" 
                    name="enableMainTable" 
                    id="enableMainTable" 
                    checked={formData.enableMainTable} 
                    onChange={(e) => onFormChange(e,'enableMainTable')}
                    className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-hba-accent-purple/30 dark:peer-focus:ring-hba-accent-pink/50 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-hba-accent-purple"></div>
                </label>
            </div>

            {formData.enableMainTable && (
                <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-hba-accent-purple/50">
                    <div>
                        <label htmlFor="mainTableWidth" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Ancho (m)</label>
                        <input type="number" name="mainTableWidth" id="mainTableWidth" value={formData.mainTableData.width} onChange={(e) => handleMainTableInputChange(e, 'width')} min="1" step="0.1" className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} />
                    </div>
                    <div>
                        <label htmlFor="mainTableLength" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Prof. (m)</label>
                        <input type="number" name="mainTableLength" id="mainTableLength" value={formData.mainTableData.length} onChange={(e) => handleMainTableInputChange(e, 'length')} min="0.5" step="0.1" className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} />
                    </div>
                    <div className="col-span-2">
                        <label htmlFor="mainTableSeats" className={`block text-xs font-medium mb-1 ${labelTextClass}`}>Asientos Mesa Principal</label>
                        <input type="number" name="mainTableSeats" id="mainTableSeats" value={formData.mainTableData.seats} onChange={(e) => handleMainTableInputChange(e, 'seats')} min="1" className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} />
                    </div>
                </div>
            )}
        </div>

        {/* Custom Elements Configuration */}
        <div className="border-t pt-4 border-gray-500/30">
          <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${labelTextClass}`}>
            <span className="text-lg">🏗️</span> Elementos Personalizados
          </h3>
          {formData.customElements && formData.customElements.map((element, index) => (
            <div key={element.id} className={`p-3 mb-3 rounded-md border ${inputBgClass} space-y-2 relative`}>
              <div className="flex justify-between items-center">
                <span className={`text-xs font-bold uppercase tracking-wider ${sliderValueTextClass}`}>Elemento {index + 1}</span>
                <button 
                  type="button" 
                  onClick={() => onRemoveCustomElement(element.id)} 
                  className={`${iconButtonClass} text-red-500 hover:text-red-700`}
                  aria-label="Eliminar elemento personalizado"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              <div>
                <input 
                  type="text" 
                  name={`customElements.${index}.name`}
                  id={`customElementName-${index}`} 
                  value={element.name} 
                  onChange={(e) => handleCustomElementChange(e, index, 'name')} 
                  placeholder="Nombre (Ej. Barra, DJ)"
                  className={`w-full p-2 text-sm border rounded-md ${inputBgClass} font-medium ${theme === 'dark' ? 'text-hba-text-primary placeholder-gray-500' : 'text-gray-900'}`}
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-[10px] uppercase font-bold mb-1 ${labelTextClass} opacity-70`}>Ancho (m)</label>
                  <input 
                    type="number" 
                    name={`customElements.${index}.width`}
                    id={`customElementWidth-${index}`} 
                    value={element.width} 
                    onChange={(e) => handleCustomElementChange(e, index, 'width')} 
                    min="0.1" step="0.1" placeholder="2.0"
                    className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} 
                  />
                </div>
                <div>
                  <label className={`block text-[10px] uppercase font-bold mb-1 ${labelTextClass} opacity-70`}>Alto (m)</label>
                  <input 
                    type="number" 
                    name={`customElements.${index}.height`}
                    id={`customElementHeight-${index}`} 
                    value={element.height} 
                    onChange={(e) => handleCustomElementChange(e, index, 'height')} 
                    min="0.1" step="0.1" placeholder="1.0"
                    className={`w-full p-2 text-sm border rounded-md ${inputBgClass} ${theme === 'dark' ? 'text-hba-text-primary' : 'text-gray-900'}`} 
                  />
                </div>
              </div>
            </div>
          ))}
          <button 
            type="button" 
            onClick={onAddCustomElement} 
            className={`w-full py-2 px-4 mt-2 border-2 border-dashed rounded-md font-medium transition-colors flex items-center justify-center text-sm
              ${theme === 'dark' 
                ? 'border-hba-light-purple text-hba-accent-pink hover:bg-hba-light-purple/30 hover:border-hba-accent-pink' 
                : 'border-gray-400 text-hba-accent-purple hover:bg-purple-50 hover:border-hba-accent-purple'}`}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Añadir Elemento
          </button>
        </div>
        
        <div className="pt-6 space-y-3 border-t border-gray-500/30">
           <button type="button" onClick={onGeneratePlan} className={primaryButtonClass}>
            Generar Plano
          </button>
          <button type="button" onClick={onResetPlan} className={secondaryButtonClass}>
            Restablecer Todo
          </button>
        </div>
      </form>
    </div>
  );
};

export default InputForm;
